// React Query hooks for ancillary fees management

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as tuitionService from '@/services/tuitionService';
import type {
  AncillaryFeeQuery,
  AssignmentsQuery,
  CreateAncillaryFeeData,
  UpdateAncillaryFeeData,
  AssignToStudentsData,
  AssignToFiliereData,
  AssignToCampusData,
} from '@/types/tuition.types';

// ==================== Query Keys ====================

export const ancillaryFeeKeys = {
  all: ['ancillary-fees'] as const,
  lists: () => [...ancillaryFeeKeys.all, 'list'] as const,
  list: (query: AncillaryFeeQuery) => [...ancillaryFeeKeys.lists(), query] as const,
  details: () => [...ancillaryFeeKeys.all, 'detail'] as const,
  detail: (id: string) => [...ancillaryFeeKeys.details(), id] as const,
  assignments: (feeId: string) => [...ancillaryFeeKeys.all, 'assignments', feeId] as const,
  assignmentsList: (feeId: string, query: AssignmentsQuery) =>
    [...ancillaryFeeKeys.assignments(feeId), query] as const,
};

// ==================== Query Hooks ====================

/**
 * Fetch ancillary fees with pagination and filtering
 */
export function useAncillaryFees(query: AncillaryFeeQuery = {}) {
  return useQuery({
    queryKey: ancillaryFeeKeys.list(query),
    queryFn: () => tuitionService.fetchAncillaryFees(query),
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Fetch a single ancillary fee by ID
 */
export function useAncillaryFee(id: string | null) {
  return useQuery({
    queryKey: ancillaryFeeKeys.detail(id || ''),
    queryFn: () => tuitionService.fetchAncillaryFee(id!),
    enabled: !!id,
  });
}

/**
 * Fetch assignments for a specific fee
 */
export function useFeeAssignments(feeId: string | null, query: AssignmentsQuery = {}) {
  return useQuery({
    queryKey: ancillaryFeeKeys.assignmentsList(feeId || '', query),
    queryFn: () => tuitionService.fetchFeeAssignments(feeId!, query),
    enabled: !!feeId,
    staleTime: 30000,
  });
}

// ==================== Mutation Hooks ====================

/**
 * Create a new ancillary fee
 */
export function useCreateAncillaryFee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAncillaryFeeData) => tuitionService.createAncillaryFee(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ancillaryFeeKeys.all });
    },
  });
}

/**
 * Update an ancillary fee
 */
export function useUpdateAncillaryFee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAncillaryFeeData }) =>
      tuitionService.updateAncillaryFee(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ancillaryFeeKeys.all });
      queryClient.invalidateQueries({ queryKey: ancillaryFeeKeys.detail(id) });
    },
  });
}

/**
 * Archive an ancillary fee
 */
export function useArchiveAncillaryFee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tuitionService.archiveAncillaryFee(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ancillaryFeeKeys.all });
    },
  });
}

/**
 * Restore an archived ancillary fee
 */
export function useRestoreAncillaryFee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tuitionService.restoreAncillaryFee(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ancillaryFeeKeys.all });
    },
  });
}

// ==================== Assignment Mutation Hooks ====================

/**
 * Assign a fee to specific students
 */
export function useAssignToStudents() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AssignToStudentsData) => tuitionService.assignToStudents(data),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ancillaryFeeKeys.assignments(data.ancillaryFeeId) });
      queryClient.invalidateQueries({ queryKey: ancillaryFeeKeys.all });
    },
  });
}

/**
 * Assign a fee to a filière (optionally with level)
 */
export function useAssignToFiliere() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AssignToFiliereData) => tuitionService.assignToFiliere(data),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ancillaryFeeKeys.assignments(data.ancillaryFeeId) });
      queryClient.invalidateQueries({ queryKey: ancillaryFeeKeys.all });
    },
  });
}

/**
 * Assign a fee to a campus
 */
export function useAssignToCampus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AssignToCampusData) => tuitionService.assignToCampus(data),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ancillaryFeeKeys.assignments(data.ancillaryFeeId) });
      queryClient.invalidateQueries({ queryKey: ancillaryFeeKeys.all });
    },
  });
}

/**
 * Remove an assignment
 */
export function useRemoveAssignment(feeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignmentId: string) => tuitionService.removeAssignment(assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ancillaryFeeKeys.assignments(feeId) });
      queryClient.invalidateQueries({ queryKey: ancillaryFeeKeys.all });
    },
  });
}
